import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";

import {
    BehaviorSubject,
    Observable,
    Subscription,
    combineLatest,
    filter,
} from "rxjs";

import { BasketItem } from "@hermes/api-model-basket";
import { Product, ProductSizeVariant } from "@hermes/api-model-product";
import {
    FeatureFlagFacade,
    LEAD_TIME_FROM_INVENTORY,
} from "@hermes/states/flipper";
import {
    isBikini,
    isDoubleObject,
    isGiftSet,
    isLook,
} from "@hermes/utils-generic/helpers";

type ProductLike = Product | ProductSizeVariant | BasketItem;

@Component({
    selector: "h-product-delivery-details",
    templateUrl: "./product-delivery-details.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDeliveryDetailsComponent implements OnInit, OnDestroy {
    @Input()
    public showShippingInformationBackground: boolean = false;

    @Input()
    public showLeadTimeBackground: boolean = false;

    public subscription: Subscription = new Subscription();

    public product$: Observable<ProductLike>;
    public shippingInformation$: Observable<string | undefined>;

    public isLeadTimeActivated$ = this.featureFlagFacade.isActivated(
        LEAD_TIME_FROM_INVENTORY,
    );

    public shouldDisplayLeadTime$: BehaviorSubject<boolean> =
        new BehaviorSubject(false);
    public shouldDisplayShippingDelay$: BehaviorSubject<boolean> =
        new BehaviorSubject(false);
    public shouldDisplayShippingInformation$: BehaviorSubject<boolean> =
        new BehaviorSubject(false);

    private product$$: BehaviorSubject<ProductLike | undefined> =
        new BehaviorSubject<ProductLike | undefined>(undefined);
    private shippingInformation$$: BehaviorSubject<string | undefined> =
        new BehaviorSubject<string | undefined>(undefined);

    constructor(private featureFlagFacade: FeatureFlagFacade) {
        this.product$ = this.product$$.asObservable().pipe(filter(Boolean));
        this.shippingInformation$ = this.shippingInformation$$.asObservable();
    }

    @Input()
    public set product(inputProduct: ProductLike) {
        this.product$$.next(inputProduct);
    }

    @Input()
    public set shippingInformation(
        inputShippingInformation: string | undefined,
    ) {
        this.shippingInformation$$.next(inputShippingInformation);
    }

    public ngOnInit(): void {
        this.subscription.add(
            combineLatest({
                product: this.product$,
                shippingInformation: this.shippingInformation$,
                isLeadTimeActivated: this.isLeadTimeActivated$,
            }).subscribe({
                next: ({ product, shippingInformation, isLeadTimeActivated }) =>
                    this.initOrUpdateDisplayRules(
                        product,
                        shippingInformation,
                        isLeadTimeActivated,
                    ),
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Method used to change the ambiguous type to product type or BasketItem type
     * This method is a hack used while the shipping delay has not been implemented for the Look type products
     * @param product The product object with an ambiguous type
     * @returns The same object but with the correct type
     */
    public castToProductOrBasketItem(
        product: ProductLike,
    ): Product | BasketItem {
        return product as Product | BasketItem;
    }

    private initOrUpdateDisplayRules(
        product: ProductLike,
        shippingInformation: string | undefined,
        isLeadTimeActivated: boolean,
    ): void {
        if (
            isGiftSet(product.sku) ||
            isBikini(product.sku) ||
            isDoubleObject(product.sku) ||
            isLook(product.sku)
        ) {
            this.buildDisplaysWithNoShippingDelay(
                product,
                shippingInformation,
                isLeadTimeActivated,
            );
        } else {
            this.buildDisplays(
                product,
                shippingInformation,
                isLeadTimeActivated,
            );
        }
    }

    private buildDisplaysWithNoShippingDelay(
        product: ProductLike,
        shippingInformation: string | undefined,
        isLeadTimeActivated: boolean,
    ): void {
        const isShared = this.isProductLikeShared(product);
        this.shouldDisplayLeadTime$.next(
            isLeadTimeActivated && Boolean(product.leadTime) && !isShared,
        );

        this.shouldDisplayShippingInformation$.next(
            !isShared && !!shippingInformation,
        );
        this.shouldDisplayShippingDelay$.next(false);
    }

    private buildDisplays(
        product: ProductLike,
        shippingInformation: string | undefined,
        isLeadTimeActivated: boolean,
    ): void {
        const isShared = this.isProductLikeShared(product);
        this.shouldDisplayLeadTime$.next(
            isLeadTimeActivated && Boolean(product.leadTime) && !isShared,
        );

        this.shouldDisplayShippingInformation$.next(
            !isShared && !!shippingInformation,
        );
        this.shouldDisplayShippingDelay$.next(!!isShared);
    }

    private isProductLikeShared(product: ProductLike): boolean {
        if (
            product instanceof Product ||
            product instanceof ProductSizeVariant
        ) {
            return product.stock?.shared ?? false;
        }

        return Boolean(product.hasSharedStock);
    }
}
